<template>
  <fullscreen-dialog
    v-model="model"
    :title="
      isPurchaseOrder ? $t('warehouse.purchase_order') : $t('warehouse.request_quote')
    "
    icon="mdi-file-document-edit"
    color="green accent-4"
    :save-text="$t('order')"
    :disabled="!valid || !editedItem.groceries.length"
    :loading="loading"
    @save="save"
  >
    <template v-slot:app-bar-actions>
      <warehouse-button-print-menu @print="print" />
    </template>

    <template v-if="editedItem?.id" v-slot:footer-actions>
      <v-btn
        color="green accent-4"
        depressed
        large
        dark
        @click="update"
      >
        <v-icon left>mdi-content-save-edit</v-icon>
        {{ $t('update') }}
      </v-btn>
    </template>

    <v-form ref="form" v-model="valid">
      <v-row>
        <v-col cols="12" lg="3" xl="2">
          <choose-warehouse-field
            hide-details
            v-model="editedItem.warehouse"
            color="white"
            return-object
          />
        </v-col>
        <v-col cols="12" lg="3" xl="2">
          <text-field
            :label="$t('warehouse.document_number')"
            v-model="editedItem.number"
            hide-details
            color="white"
          />
        </v-col>
        <v-col cols="12" lg="3" xl="2">
          <date-picker
            v-model="editedItem.document_date"
            single-date
            color="white"
          />
        </v-col>
        <v-col cols="12" lg="3" xl="2">
          <datetime-picker
            v-model="editedItem.offer_deadline"
            single-date
            :label="$t('warehouse.offers_deadline')"
            color="white"
          />
        </v-col>
        <v-col cols="12" lg="3" xl="2">
          <datetime-picker
            v-model="editedItem.delivery_deadline"
            single-date
            :label="$t('warehouse.delivery_deadline')"
            color="white"
          />
        </v-col>
        <v-col cols="12" lg="3" xl="2">
          <autocomplete
            v-model="editedItem.ordered_by"
            :items="users"
            item-text="fullName"
            item-value="id"
            :loading="loadingUsers"
            :label="$t('warehouse.ordered_by')"
            required
            :rules="[rules.required]"
            color="white"
          />
        </v-col>
        <v-col cols="12" lg="3" xl="2">
          <choose-supplier-field
            hide-details
            v-model="editedItem.supplier"
            color="white"
          />
        </v-col>
        <v-col cols="12" lg="3" xl="4">
          <text-area
            :label="$t('note')"
            v-model="editedItem.notes"
            rows="1"
            color="white"
          />
        </v-col>
      </v-row>
    </v-form>

    <warehouse-document-groceries-table
      v-if="selectedWarehouse"
      :groceries="editedItem.groceries"
      :warehouse="selectedWarehouse"
      :headers="headers"
      @removeGrocery="removeGrocery"
    />
  </fullscreen-dialog>
</template>

<script>
import hasWarehouseDocumentDialog from "@/mixins/warehouse/hasWarehouseDocumentDialog";
import hasPrintingV2 from "@/mixins/hasPrintingV2";
import ChooseSupplierField from "@/components/warehouse/fields/ChooseSupplierField.vue";
import { orderDocumentGrocery, requestOrderDocumentGrocery } from "@/models/warehouse";
import warehouseService from "@/services/warehouse";

export default {
  name: "WarehousePurchaseOrderDialog",
  mixins: [hasWarehouseDocumentDialog, hasPrintingV2],
  components: {
    ChooseSupplierField,
  },
  props: {
    editedItem: {
      type: Object,
      default: () => orderDocumentGrocery,
    },
    isPurchaseOrder: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      datetime: new Date().toISOString(),
    };
  },
  methods: {
    update() {
      this.showInfoSnackbar("Ažuriranje narudžbenice je još u razvoju. Funkcionalnost će biti dostupna uskoro.");
    },
    print(withStock = false) {
      const {
        row,
        col,
        field,
        text,
        bold,
        table,
        line,
        spacer,
        heading,
        label,
      } = this.printElements;

      const docDate = this.localeDate(this.editedItem.document_date);
      const docNumber = this.editedItem.number || "";
      const orderedBy = this.users.find((user) => user.id === this.editedItem.ordered_by);
      const orderedByName = this.toLatin(
        [orderedBy?.name, orderedBy?.lastname].filter(Boolean).join(" ")
      );

      const companyBlock = (c, title) =>
        col([
          bold(title, 16),
          spacer(8),
          label(this.$t("company_name") + ":", c?.name || ""),
          label(this.$t("address") + ":", c?.address || ""),
          label(this.$t("phone") + ":", c?.phone || ""),
          label(this.$t("email") + ":", c?.email || ""),
        ]);

      this.printDoc(
        [
          // Companies
          row("between", [
            companyBlock(this.company, this.$t("warehouse.orderer")),
            companyBlock(this.editedItem.supplier, this.$t("supplier")),
          ]),
          // Date
          spacer(14),
          text(this.$t("date") + ": " + docDate, { size: 16, block: true }),
          // Title
          spacer(14),
          heading(this.$t("request_for_quote_no") + " " + docNumber),
          // Table
          spacer(10),
          table(this.getPrintHeaders(withStock), this.getPrintItems()),
          // Deadlines
          spacer(14),
          text(this.$t("warehouse.offers_deadline") + ":", { size: 16, block: true }),
          text(this.$t("warehouse.delivery_deadline") + ":", { size: 16, block: true }),
          // Signature
          spacer(40),
          row("end", [
            col("center", [
              text(this.$t("warehouse.for_orderer")),
              spacer(16),
              line(),
              text(orderedByName),
            ]),
          ]),
        ],
        { pageTitle: this.$t("request_for_quote_no") + " " + docNumber }
      );
    },
    async save() {
      const params = {
        ...this.editedItem,
        warehouse_id: this.editedItem.warehouse?.id,
        warehouse: this.editedItem.warehouse,
        supplier_id: this.editedItem.supplier?.id || null,
        groceries: this.editedItem.groceries.map((grocery) => ({
          grocery_id: grocery.id,
          warehouse_id: this.editedItem.warehouse?.id,
          name: grocery.name,
          code: grocery.code,
          supplier_code: grocery.supplier_code,
          measure: grocery.measure,
          amount: grocery.amount,
          gross_quantity: Number(grocery.gross_quantity) || 0,
          unit_price: Number(grocery.unit_price) || 0,
          transaction_type: this.editedItem.document_type,
        })),
      };
      const request = warehouseService.addDocumentTransaction(params);

      try {
        this.loading = true;
        await this.handleRequest(request, {
          successMessage: this.isPurchaseOrder
            ? this.$t("purchase_order_created_successfully")
            : this.$t("request_quote_created_successfully"),
          errorMessagePrefix: this.$t("error"),
        });
        this.model = false;
        this.$emit("success");
      } catch (error) {
        console.error("Save operation failed:", error);
      } finally {
        this.loading = false;
      }
    },
  },
  computed: {
    company() {
      return this.$store.state.restaurantStore;
    },
    filters() {
      return this.$store.state.warehouse.filters;
    },
    headers() {
      const base = [
        { text: this.$t("r_b"), value: "num", width: "100px" },
        { text: this.$t("code"), align: "start", value: "code", width: "150px" },
        { text: this.$t("title"), value: "name" },
        {
          text: this.$t("quantity") + ` (${this.$t("warehouse.quantity_gross")})`,
          value: "gross_quantity",
          align: "right",
          width: "160px",
        },
        {
          text: this.$t("quantity") + ` (${this.$t("warehouse.quantity_net")})`,
          value: "amount",
          align: "right",
          width: "160px",
        },
        {
          text: this.$t("warehouse.warehouse_status"),
          value: "warehouse_stock",
          print: false,
          align: "right",
          width: "200px",
        },
        {
          text: this.$t("unit_of_measurment"),
          printText: this.$t("unit_of_measurment").replace(" ", "<br>"),
          value: "measure",
          width: "200px",
        },
      ];
      if (this.isPurchaseOrder) {
        base.push(
          { text: this.$t("price"), value: "unit_price", align: "right", width: "140px" },
          { text: this.$t("amount"), value: "total_amount", align: "right", width: "140px", sortable: false }
        );
      }
      base.push({ text: "", value: "actions", width: "100px" });
      return base;
    },
  },
  watch: {
    model(newVal) {
      if (newVal) {
        if (!this.editedItem.id) {
          let note = `Po osnovu trebovanja za period od ${this.filters.dates.from} do ${this.filters.dates.to}`;
          if (this.filters.dates.from === this.filters.dates.to) {
            note = `Po osnovu trebovanja za ${this.filters.dates.from}`;
          }
          this.editedItem.notes = this.translate(note);
        } else {
          this.editedItem.supplier = {
            id: this.editedItem.supplier_id,
            name: this.editedItem.supplier,
          };
        }
      }
    },
    isPurchaseOrder: {
      handler() {
        if (this.editedItem.id) return;
        this.editedItem.document_type = this.isPurchaseOrder
          ? orderDocumentGrocery.document_type
          : requestOrderDocumentGrocery.document_type;
      },
      immediate: true,
    },
  },
};
</script>
